import { useState } from "react";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { addAddress } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import Button from "components/atoms/Button/Button";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import { appendDefaultOption } from "helpers/commonHelpers";
import FormField from "components/molecules/FormField/FormField";
import { ReactComponent as EditIcon } from "assets/images/icons/Edit.svg";
import testIds from "constants/testIds";
import SearchSelect from "../Common/SearchSelect";
import { IPickupDeliveryEditAddressProps } from "./Types";

function PickupDeliveryEditAddress(props: IPickupDeliveryEditAddressProps) {
  const {
    isPickupOrDelivery,
    isQuoteRequest,
    isOffPlatformShipment,
    countryStates,
    formState,
    countries,
    isEditConsolidatedShipment,
  } = props;
  const methods = useFormContext();
  const intl = useIntl();

  const { setValue, getValues, watch, control } = methods;

  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [editAddress, setEditAddress] = useState(false);

  const { permissions }: any = usePermissions();
  const { address_availability } = JSON.parse(permissions).company_shipper_settings;
  const addressBookPermissions = permissions ? JSON.parse(permissions).permissions.quoting : null;
  const restrictedToAddressBook = isOffPlatformShipment ? false : addressBookPermissions?.restricted_to_address_book;
  const invitedModes = useWatch({ name: "invited_modes", control });
  const addressType = getValues(`schedule.${isPickupOrDelivery}_address_type`);

  const selectedTimezone = watch(`schedule.${isPickupOrDelivery}_timezone`);
  const addressName = watch(`schedule.${isPickupOrDelivery}_address_name`);
  const street = watch(`schedule.${isPickupOrDelivery}_street`);
  const addressLine2 = watch(`schedule.${isPickupOrDelivery}_street_line2`);
  const zipcode = watch(`schedule.${isPickupOrDelivery}_zipcode`);
  const city = watch(`schedule.${isPickupOrDelivery}_city`);
  const state = watch(`schedule.${isPickupOrDelivery}_state`);
  const country = watch(`schedule.${isPickupOrDelivery}_country`);

  const handleDisabledSave = () => {
    if (!isOffPlatformShipment && (addressName === "" || street === "" || zipcode === "" || city === "" || country === "")) {
      return true;
    }
    if (isOffPlatformShipment && (addressName === "" || city === "" || country === "")) {
      return true;
    }
  };

  const handleEditAddress = () => {
    if (getValues("saveToAddressBook")) saveToAddressBook();
    else setEditAddress(!editAddress);
  };

  const saveToAddressBook = () => {
    const paymentLocation = getValues("organizational.location");
    setIsSavingAddress(true);

    const payload = {
      company_name: addressName,
      country_iso: countries.find((currentCountry) => currentCountry.label === country)?.value,
      postal_code: zipcode,
      address_line_1: street,
      address_line_2: addressLine2,
      timezone: selectedTimezone,
      city,
      ...(address_availability === "Payment Location" && { payment_location: paymentLocation }),
      ...(state && { state_short: countryStates.find((countryState) => countryState.label === state)?.code }),
    };

    addAddress(payload)
      .then(() => {
        toast("Address saved to address book", {
          type: "success",
        });
        setEditAddress(!editAddress);
        setValue("saveToAddressBook", false);
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSavingAddress(false));
  };

  return (
    <div>
      {!editAddress &&
        (isEditConsolidatedShipment ||
          !isQuoteRequest ||
          getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address") && (
          <div>
            <div className="flex-head-blue d-flex justify-content-between">
              <div>{addressName && getValues(`schedule.${isPickupOrDelivery}_address_name`)}</div>
              {!isEditConsolidatedShipment && (
                <div>
                  {country ? (
                    <div className="ml-2 cursor-pointer">
                      <EditIcon onClick={handleEditAddress} />
                    </div>
                  ) : (
                    addressName && (
                      <div className="ml-2 cursor-pointer text-blue-200" onClick={handleEditAddress}>
                        <FormattedMessage id="general.addManualAddress" defaultMessage="Add manual address" />
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
            {country && (
              <div>
                {!isOffPlatformShipment &&
                  (isEditConsolidatedShipment ||
                    getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address" ||
                    !isQuoteRequest) && (
                    <>
                      <p className="mb-0">{street}</p>
                      <p className="mb-0">{addressLine2}</p>
                      {zipcode && `${zipcode}, `}
                    </>
                  )}
                {city && `${city}, `}
                {state && `${state}, `}
                {country && `${country}`}
              </div>
            )}
          </div>
        )}
      {(editAddress ||
        (isQuoteRequest && ["seaport", "airport"].includes(getValues(`schedule.${isPickupOrDelivery}_address_type`)))) && (
        <>
          <div className="flex-row">
            {(getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address" || !isQuoteRequest) && (
              <div className="w-192 mb-xl">
                <FormField
                  id="CompanyName"
                  label={intl.formatMessage({
                    id: "general.companyName",
                    defaultMessage: "Company name",
                  })}
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.comapnyNameInfo",
                    defaultMessage: "Type company name here..",
                  })}
                  disabled={
                    restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_address_name`]
                  }
                  field_id={`schedule.${isPickupOrDelivery}_address_name`}
                  mandatory
                  validations={{
                    validate: {
                      ...(invitedModes?.parcel && {
                        maxCharacters: (v: any) => v.length <= 35 || "Maximum characters exceeded (35)",
                      }),
                    },
                  }}
                  onChange={(e) => {
                    setValue(`schedule.${isPickupOrDelivery}_address_name`, e.target.value);
                  }}
                />
              </div>
            )}
            {!isOffPlatformShipment &&
              (getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address" || !isQuoteRequest) && (
                <>
                  <div className="w-192">
                    <FormField
                      id="id_pickup_street"
                      label={intl.formatMessage({
                        id: "new-request.schedule.addressPlaceholder",
                        defaultMessage: "Address line 1",
                      })}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "new-request.schedule.addressPlaceholder",
                        defaultMessage: "Address line 1",
                      })}
                      field_id={`schedule.${isPickupOrDelivery}_street`}
                      disabled={restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_street`]}
                      mandatory
                      validations={{
                        validate: {
                          ...(invitedModes?.parcel && {
                            maxCharacters: (v: any) => v.length <= 35 || "Maximum characters exceeded (35)",
                          }),
                        },
                      }}
                      onChange={(e) => {
                        setValue(`schedule.${isPickupOrDelivery}_street`, e.target.value);
                      }}
                    />
                  </div>
                  <div className="w-192">
                    <FormField
                      id="id_pickup_street_line_two"
                      type="textinput"
                      label={intl.formatMessage({
                        id: "new-request.schedule.addressLineTwoPlaceholder",
                        defaultMessage: "Address line 2",
                      })}
                      placeholder={intl.formatMessage({
                        id: "new-request.schedule.addressLineTwoPlaceholder",
                        defaultMessage: "Address line 2",
                      })}
                      disabled={
                        restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_street_line2`]
                      }
                      field_id={`schedule.${isPickupOrDelivery}_street_line2`}
                      validations={{
                        validate: {
                          ...(invitedModes?.parcel && {
                            maxCharacters: (v: any) => !v || v?.length <= 35 || "Maximum characters exceeded (35)",
                          }),
                        },
                      }}
                      onChange={(e) => {
                        setValue(`schedule.${isPickupOrDelivery}_street_line2`, e.target.value);
                      }}
                    />
                  </div>
                </>
              )}
          </div>
          <div className="flex-row mb-xl">
            <div className="w-192">
              <div className="d-flex flex-column">
                <label className="label required">
                  <FormattedMessage id="general.country" defaultMessage="Country" />
                </label>
                <SearchSelect
                  name={`schedule.${isPickupOrDelivery}_country`}
                  defaultValue={getValues(`schedule.pickup_country`)}
                  placeholder={intl.formatMessage({
                    id: "general.country",
                    defaultMessage: "Country",
                  })}
                  disable={restrictedToAddressBook}
                  options={countries}
                  onSelect={(item: any) => {
                    setTimeout(() => {
                      setValue(`schedule.${isPickupOrDelivery}_country`, item?.[0]?.label || "");
                      setValue(`schedule.${isPickupOrDelivery}_country_code`, item?.[0]?.value || "");
                    }, 0);
                  }}
                  required
                />
              </div>
            </div>
            {(isQuoteRequest || isOffPlatformShipment ? addressType === "address" : true) &&
              (countryStates?.length > 0 || getValues(`schedule.${isPickupOrDelivery}_state`)) && (
                <div className="w-192">
                  <label className="label required">
                    <FormattedMessage id="new-request.schedule.stateOrProvince" defaultMessage="State / Province" />
                  </label>
                  <SearchSelect
                    name={`schedule.${isPickupOrDelivery}_state`}
                    placeholder={intl.formatMessage({
                      id: "general.state",
                      defaultMessage: "State",
                    })}
                    disable={restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_state`]}
                    defaultValue={getValues(`schedule.${isPickupOrDelivery}_state`)}
                    options={appendDefaultOption(countryStates, intl)}
                    onSelect={(item: any) => {
                      setTimeout(() => {
                        setValue(`schedule.${isPickupOrDelivery}_state`, item?.[0]?.label || "");
                      }, 0);
                    }}
                    required
                  />
                </div>
              )}

            <div className="w-192">
              <FormField
                id="locality"
                label={intl.formatMessage({
                  id: "general.city",
                  defaultMessage: "City",
                })}
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "new-request.schedule.cityInfo",
                  defaultMessage: "Type city here..",
                })}
                field_id={`schedule.${isPickupOrDelivery}_city`}
                mandatory
                validations={{
                  validate: {
                    ...(invitedModes?.parcel && {
                      maxCharacters: (v: any) => v.length <= 20 || "Maximum characters exceeded (20)",
                    }),
                  },
                }}
                disabled={restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_city`]}
                onChange={(e) => {
                  setValue(`schedule.${isPickupOrDelivery}_city`, e.target.value);
                }}
              />
            </div>
            {(getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address" || !isQuoteRequest) && (
              <div className="w-192">
                <FormField
                  label={intl.formatMessage({
                    id: "new-request.schedule.postalCode",
                    defaultMessage: "Postal code",
                  })}
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.zipcodeInfo",
                    defaultMessage: "Type postal code here..",
                  })}
                  field_id={`schedule.${isPickupOrDelivery}_zipcode`}
                  mandatory={!isOffPlatformShipment}
                  disabled={restrictedToAddressBook || formState.disabledFields[`schedule.${isPickupOrDelivery}_zipcode`]}
                  onChange={(e) => {
                    setValue(`schedule.${isPickupOrDelivery}_zipcode`, e.target.value);
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}

      {editAddress && (
        <div className="flex-row">
          <div className="w-90">
            {addressBookPermissions?.allow_address_book_entry &&
              !isOffPlatformShipment &&
              (getValues(`schedule.${isPickupOrDelivery}_address_type`) === "address" || !isQuoteRequest) && (
                <div className="d-flex mb-xs">
                  <Checkbox name="saveToAddressBook" required={false} providerSections />
                  <label className="no-wrap">
                    <FormattedMessage id="new-request.schedule.saveToAddressBook" defaultMessage="Save to address book" />
                  </label>
                </div>
              )}
            <Button
              variant="primary"
              className="rounded-pill blue-200 h-26"
              label={intl.formatMessage({
                id: "general.save",
                defaultMessage: "Save",
              })}
              onClick={handleEditAddress}
              isLoading={isSavingAddress}
              disabled={handleDisabledSave()}
              data-testid={testIds.FORM_ADDRESS_SAVE_BUTTON}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PickupDeliveryEditAddress;
